import React from "react";
import keycloak from "./keycloak";
import { setupAxiosAuth } from "../axiosSetup";

const AuthContext = React.createContext(null);

export function useAuth() {
  const ctx = React.useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}

function hasOidcCallbackParams() {
  return (
    window.location.search.includes("code=") ||
    window.location.search.includes("state=") ||
    window.location.search.includes("session_state=") ||
    window.location.hash.includes("code=") ||
    window.location.hash.includes("state=") ||
    window.location.hash.includes("session_state=")
  );
}

function stripOidcParamsFromUrl() {
  const cleanUrl = `${window.location.pathname}${window.location.hash || ""}`;
  window.history.replaceState({}, document.title, cleanUrl);
}

function clearKeycloakBrowserState() {
  try {
    const ssKeys = Object.keys(sessionStorage);
    for (const k of ssKeys) {
      if (
        k.startsWith("kc-") ||
        k.startsWith("KEYCLOAK") ||
        k.includes("kc-callback")
      ) {
        sessionStorage.removeItem(k);
      }
    }

    const lsKeys = Object.keys(localStorage);
    for (const k of lsKeys) {
      if (
        k.startsWith("kc-") ||
        k.startsWith("KEYCLOAK") ||
        k.includes("kc-callback")
      ) {
        localStorage.removeItem(k);
      }
    }
  } catch {
    // ignore
  }
}

function getCurrentRedirectUri() {
  return `${window.location.origin}${window.location.pathname}${window.location.search}${window.location.hash}`;
}

export function AuthProvider({ children }) {
  const didInit = React.useRef(false);
  const didSetupAxios = React.useRef(false);

  const [ready, setReady] = React.useState(false);
  const [authenticated, setAuthenticated] = React.useState(false);

  const getToken = React.useCallback(async () => {
    if (!keycloak.authenticated) return null;
    try {
      await keycloak.updateToken(30);
    } catch {
      // ignore error for now
    }
    return keycloak.token || null;
  }, []);

  const login = React.useCallback(
    () =>
      keycloak.login({
        redirectUri: getCurrentRedirectUri(),
      }),
    []
  );

  const logout = React.useCallback(
    () =>
      keycloak.logout({
        redirectUri: window.location.origin + "/",
      }),
    []
  );

  React.useEffect(() => {
    let cancelled = false;

    if (didInit.current) return;
    didInit.current = true;

    keycloak.onAuthLogout = () => console.log("[KC] onAuthLogout");

    keycloak.onTokenExpired = async () => {
      console.log("[KC] onTokenExpired");
      try {
        await keycloak.updateToken(30);
      } catch (e) {
        console.warn("[KC] token refresh onTokenExpired failed:", e);
      }
    };

    (async () => {
      try {
        const auth = await keycloak.init({
          onLoad: "login-required",
          pkceMethod: "S256",
          flow: "standard",
          responseMode: "query",
          scope: "openid profile email",
          redirectUri: getCurrentRedirectUri(),
          checkLoginIframe: false,
          enableLogging: true,
        });

        if (cancelled) return;

        if (hasOidcCallbackParams()) stripOidcParamsFromUrl();

        setAuthenticated(!!auth);
        setReady(true);
      } catch (err) {
        if (cancelled) return;

        console.error("[KC] init rejected (raw):", err);
        console.error("[KC] init rejected (string):", String(err));
        console.error("[KC] location:", window.location.href);

        const retryKey = "kc_nonce_retry_once";
        const alreadyRetried = sessionStorage.getItem(retryKey) === "1";

        if (!alreadyRetried) {
          sessionStorage.setItem(retryKey, "1");
          stripOidcParamsFromUrl();
          clearKeycloakBrowserState();
          setReady(true);
          setAuthenticated(false);
          return;
        }

        sessionStorage.removeItem(retryKey);
        stripOidcParamsFromUrl();
        clearKeycloakBrowserState();

        setAuthenticated(false);
        setReady(true);
      }
    })();

    const interval = setInterval(async () => {
      if (cancelled) return;
      if (!keycloak.authenticated) return;

      try {
        await keycloak.updateToken(60);
      } catch (e) {
        console.warn("[KC] updateToken failed:", e);
      }
    }, 30_000);

    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, []);

  React.useEffect(() => {
    if (!ready) return;
    if (didSetupAxios.current) return;
    didSetupAxios.current = true;

    setupAxiosAuth(getToken);
  }, [ready, getToken]);

  if (!ready) return null;

  const value = {
    ready,
    authenticated,
    login,
    logout,
    getToken,
    keycloak,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
